import { supabase } from "../lib/supabase";
const functionsUrl = import.meta.env.VITE_SUPABASE_FUNCTIONS_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// The planner displays 40 posts per page and retains at most 2,000 posts.
// Fetch in batches so Supabase's per-request row limit does not truncate the
// planner at 1,000 posts (25 pages).
const FACEBOOK_PLANNER_POST_LIMIT = 2000;
const FACEBOOK_POST_FETCH_BATCH_SIZE = 1000;

/**
 * Get the CURRENT Supabase access token for authenticated requests.
 * Always fetched fresh via supabase.auth.getSession() — never a token
 * cached earlier in component state — and if that comes back without a
 * usable token (e.g. a session that expired between page load and now),
 * one explicit refreshSession() is attempted before giving up, so a
 * merely-expired-but-refreshable session doesn't falsely report
 * "Authentication expired."
 */
async function getAccessToken() {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (session?.access_token) {
    return session.access_token;
  }

  const {
    data: { session: refreshedSession },
  } = await supabase.auth.refreshSession();
  return refreshedSession?.access_token || null;
}

/**
 * Build a stable Facebook page profile picture URL from the page ID.
 * The Graph API's /{page-id}/picture endpoint is public (no access_token
 * required) and always redirects to the page's current CDN image, unlike
 * the signed `picture.data.url` returned at connect-time (stored in
 * page_picture_url), which is a time-limited lookaside URL that expires.
 * @param {string} pageId
 * @returns {string|null}
 */
export function getFacebookPagePictureUrl(pageId) {
  if (!pageId) return null;
  return `https://graph.facebook.com/v19.0/${pageId}/picture?type=normal`;
}

/**
 * Fetch Facebook posts with optional filters
 * @param {Object} filters - Filter options (status, pageId, dateRange)
 * @returns {Promise<Array>} - List of posts
 */
export async function fetchFacebookPosts(filters = {}) {
  console.log("[facebookPostsService] Fetching posts with filters:", filters);
  try {
    const fetchedPosts = [];

    while (fetchedPosts.length < FACEBOOK_PLANNER_POST_LIMIT) {
      const from = fetchedPosts.length;
      const to = Math.min(
        from + FACEBOOK_POST_FETCH_BATCH_SIZE,
        FACEBOOK_PLANNER_POST_LIMIT,
      ) - 1;

      let query = supabase
        .from("fb_scheduled_posts")
        .select("*");
      // fb_scheduled_posts.profile_id is nullable (a handful of pre-existing
      // rows have no owner at all — see 20260801000004), so only scope when a
      // profileId is actually provided.
      if (filters.profileId) query = query.eq("profile_id", filters.profileId);

      const { data, error } = await query
        .order("scheduled_at", { ascending: false })
        .order("id", { ascending: false })
        .range(from, to);

      if (error) {
        console.error("[facebookPostsService] Error fetching posts:", error);
        throw error;
      }

      const batch = data || [];
      if (batch.length === 0) break;
      fetchedPosts.push(...batch);
    }

    let filteredPosts = fetchedPosts;

    if (filters.status && filters.status !== "all") {
      filteredPosts = filteredPosts.filter(
        (post) => post.status === filters.status
      );
    }

    if (filters.pageId) {
      filteredPosts = filteredPosts.filter(
        (post) => post.page_id === filters.pageId
      );
    }

    if (filters.startDate) {
      filteredPosts = filteredPosts.filter(
        (post) => new Date(post.scheduled_at) >= new Date(filters.startDate)
      );
    }

    if (filters.endDate) {
      filteredPosts = filteredPosts.filter(
        (post) => new Date(post.scheduled_at) <= new Date(filters.endDate)
      );
    }

    console.log("[facebookPostsService] Posts fetched:", filteredPosts.length);
    return filteredPosts;
  } catch (err) {
    console.error("[facebookPostsService] Failed to fetch posts:", err);
    throw err;
  }
}

/**
 * Create a new Facebook post (calls Edge Function to publish to Facebook)
 * @param {Object} postData - Post data
 * @returns {Promise<Object>} - Created post result
 */
export async function createFacebookPost(postData) {
  console.log("[facebookPostsService] Creating post:", postData);
  try {
    const token = await getAccessToken();
    if (!token) {
      throw new Error("Authentication expired. Please re-login.");
    }
    if (!functionsUrl) {
      throw new Error("Facebook automation service is not configured.");
    }

    const res = await fetch(`${functionsUrl}/facebook-post`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        apikey: supabaseAnonKey,
      },
      body: JSON.stringify({
        profile_id: postData.profileId || null,
        content: postData.content,
        first_comment: postData.firstComment,
        image_urls: postData.imageUrls || [],
        article_link: postData.articleLink || null,
        page_ids: postData.pageIds,
        scheduled_at: postData.scheduledTime || null,
        post_now: postData.postNow !== false,
      }),
    });

    const result = await res.json().catch(() => ({}));
    if (!res.ok) {
      const firstFailure = result?.results?.find((item) => !item.success);
      const error = new Error(firstFailure?.error || result.error || result.message || `HTTP ${res.status}: Failed to create post`);
      error.results = result?.results || [];
      throw error;
    }

    console.log("[facebookPostsService] Post created successfully:", result);
    return result;
  } catch (err) {
    console.error("[facebookPostsService] Failed to create post:", err);
    throw err;
  }
}

/**
 * Delete a Facebook post
 * @param {string} postId - Post ID
 * @returns {Promise<void>}
 */
export async function deleteFacebookPost(postId) {
  console.log("[facebookPostsService] Deleting post:", postId);
  try {
    const { error } = await supabase
      .from("fb_scheduled_posts")
      .delete()
      .eq("id", postId);

    if (error) {
      console.error("[facebookPostsService] Error deleting post:", error);
      throw error;
    }
    console.log("[facebookPostsService] Post deleted successfully");
  } catch (err) {
    console.error("[facebookPostsService] Failed to delete post:", err);
    throw err;
  }
}

/**
 * Delete multiple Facebook posts in a single bulk request
 * @param {string[]} postIds - Post IDs
 * @returns {Promise<void>}
 */
export async function deleteFacebookPosts(postIds) {
  console.log("[facebookPostsService] Bulk deleting posts:", postIds);
  try {
    const { error } = await supabase
      .from("fb_scheduled_posts")
      .delete()
      .in("id", postIds);

    if (error) {
      console.error("[facebookPostsService] Error bulk deleting posts:", error);
      throw error;
    }
    console.log("[facebookPostsService] Posts deleted successfully");
  } catch (err) {
    console.error("[facebookPostsService] Failed to bulk delete posts:", err);
    throw err;
  }
}

/**
 * Fetch user's Facebook pages from Supabase
 * @returns {Promise<Array>} - List of Facebook pages
 */
export async function fetchFacebookPages(profileId = null) {
  console.log("[facebookPostsService] Fetching Facebook pages");
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session?.user) {
      throw new Error("User not authenticated");
    }

    let query = supabase
      .from("facebook_pages")
      .select("page_id, page_name, page_picture_url, profile_id")
      .eq("user_id", session.user.id)
      .eq("page_access_token_is_valid", true);
    if (profileId) query = query.eq("profile_id", profileId);
    const { data, error } = await query.order("page_name", { ascending: true });

    if (error) {
      console.error("[facebookPostsService] Error fetching pages:", error);
      throw error;
    }

    console.log("[facebookPostsService] Pages fetched:", data?.length || 0);
    return data || [];
  } catch (err) {
    console.error("[facebookPostsService] Failed to fetch pages:", err);
    // Return empty array if error - do not show fake pages
    return [];
  }
}

/**
 * Submit a bulk-schedule batch to the background queue (Vercel Queue ->
 * facebook-post). Returns immediately once the batch + queue rows are
 * persisted and dispatched — the actual Facebook calls happen later,
 * server-side, regardless of whether this browser tab stays open.
 * @param {string} batchId - client-generated UUID (crypto.randomUUID()),
 *   makes the submission itself safe to retry without creating duplicates.
 * @param {Array} items - [{ articleId, content, firstComment, imageUrl, articleLink, scheduledTime }]
 * @param {Array<string>} pageIds
 * @param {string|null} profileId
 * @returns {Promise<{batchId: string, totalCount: number}>}
 */
export async function createBulkScheduleBatch(batchId, items, pageIds, profileId) {
  const token = await getAccessToken();
  if (!token) {
    throw new Error("Authentication expired. Please re-login.");
  }

  const res = await fetch(`/api/fb-bulk-schedule/create`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ batchId, items, pageIds, profileId }),
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${res.status}: Failed to submit bulk schedule batch`);
  }

  return res.json();
}

/**
 * Resume an existing active bulk batch by dispatching only queue rows that
 * are still pending, have never been attempted, and have no queue message.
 * @param {string} batchId
 * @returns {Promise<{batchId: string, resumed: boolean, dispatchedCount: number}>}
 */
export async function resumeBulkScheduleBatch(batchId) {
  const token = await getAccessToken();
  if (!token) {
    throw new Error("Authentication expired. Please re-login.");
  }

  const res = await fetch(`/api/fb-bulk-schedule/resume`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ batchId }),
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${res.status}: Failed to resume bulk schedule batch`);
  }

  return res.json();
}

/**
 * Fetch the most recent bulk-schedule batch for a profile — used to
 * resume/restore the persistent progress bar on mount, refresh, or
 * navigating back into the Facebook Posts Planner.
 * @param {string|null} profileId
 * @returns {Promise<Object|null>}
 */
export async function fetchLatestBulkBatch(profileId) {
  let query = supabase
    .from("fb_bulk_schedule_batches")
    .select("*")
    .order("created_at", { ascending: false })
    .limit(1);
  if (profileId) query = query.eq("profile_id", profileId);
  const { data, error } = await query;
  if (error) {
    console.error("[facebookPostsService] Error fetching latest bulk batch:", error);
    return null;
  }
  return data && data.length > 0 ? data[0] : null;
}

export async function fetchBulkBatchFailure(batchId) {
  const { data, error } = await supabase
    .from("fb_bulk_schedule_queue")
    .select("error_message")
    .eq("batch_id", batchId)
    .eq("status", "failed")
    .not("error_message", "is", null)
    .order("completed_at", { ascending: false, nullsFirst: false })
    .limit(1);
  if (error) {
    console.error("[facebookPostsService] Error fetching bulk batch failure:", error);
    return null;
  }
  return data?.[0]?.error_message || null;
}

/**
 * Fetch the current user's most recently finished, not-yet-acknowledged
 * bulk-schedule batch across all profiles — used by the global completion
 * notifier so it fires no matter which page the user is on, or if the
 * batch finished while the browser was completely closed.
 * @returns {Promise<Object|null>}
 */
export async function fetchUnacknowledgedFinishedBulkBatch() {
  const { data, error } = await supabase
    .from("fb_bulk_schedule_batches")
    .select("*")
    .in("status", ["completed", "completed_with_errors"])
    .is("acknowledged_at", null)
    .order("completed_at", { ascending: false })
    .limit(1);
  if (error) {
    console.error("[facebookPostsService] Error fetching unacknowledged bulk batch:", error);
    return null;
  }
  return data && data.length > 0 ? data[0] : null;
}

/**
 * Marks a finished batch's completion notification as seen so it doesn't
 * keep showing up (in the progress bar or the global notifier).
 * @param {string} batchId
 */
export async function acknowledgeBulkBatch(batchId) {
  const { error } = await supabase.rpc("acknowledge_fb_bulk_batch", { p_batch_id: batchId });
  if (error) {
    console.error("[facebookPostsService] Error acknowledging bulk batch:", error);
  }
}

/**
 * Upload images for post to Supabase Storage (returns public URLs)
 * @param {Array} files - File objects
 * @returns {Promise<Array>} - Array of uploaded image URLs
 */
export async function uploadPostImages(files) {
  console.log("[facebookPostsService] Uploading images:", files?.length);
  try {
    const uploadedUrls = [];
    for (const file of files) {
      const fileName = `${Date.now()}_${Math.random().toString(36).slice(2, 9)}_${file.name}`;
      const { data, error } = await supabase.storage
        .from("fb-post-images")
        .upload(fileName, file);

      if (error) {
        console.error("[facebookPostsService] Error uploading image:", error);
        throw error;
      }

      const { data: urlData } = supabase.storage
        .from("fb-post-images")
        .getPublicUrl(fileName);

      uploadedUrls.push(urlData.publicUrl);
    }
    console.log("[facebookPostsService] Images uploaded:", uploadedUrls.length);
    return uploadedUrls;
  } catch (err) {
    console.error("[facebookPostsService] Failed to upload images:", err);
    throw new Error(`Image upload failed: ${err.message || err}`);
  }
}
